import { styled } from "@linaria/react";
import { css } from "@linaria/core";
import React from "react";
import type { Project } from "../../../api/useProjects";
import { useIndexedProjects } from "../../../api/useProjects";
import { color } from "@ui";
import Circles from "@ui/Picto/Circles";
import ActivityComponent from "../../Activity";
import Filters, { useFilteredValuesByFilterType, useHideAutomaticActions } from "./Filters";
import t from "../../../i18n";
import { useActivities } from "../../../api/useActivities";
import More from "../../Activity/More";
import Spinner from "@ui/Spinner";
import RefreshButton from "../../Activity/RefreshButton";
import BlankStateV2 from "@ui/BlankStateV2";
import useHeaderHeight, { FILTER_ACTIVITY_HEIGHT_CSS_VARIABLE } from "@ui/Header/useHeaderHeight";
import { useActivityEvents } from "../../../api/useActivityEvents";
import { mapActivityEventFilterValues } from "./eventFilter";
const FilterWrapper = styled.div`
  align-items: center;
  padding: 25px;
  border-bottom: 1px solid ${color.titanium100};
  display: flex;
  justify-content: space-between;
`;

const ActivityWrapper = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: auto;
`;

const ActivityBlankCss = css`
  padding: 40px 20px;

  & > :first-child {
    height: auto;
    min-width: 0;
    padding-bottom: 8px;
  }

  & > :first-child svg {
    max-width: 220px;
    max-height: 220px;
  }
`;

interface P {
  project: Project;
  eventId: string | null;
}

function ProjectActivity({ project, eventId }: P) {
  const projectId = project.id;
  const [filteredValues, setFilteredValues] = useFilteredValuesByFilterType({
    scope: eventId ?? projectId,
    defaultValue: { types: [], authors: [], eventIds: eventId ? [eventId] : [] },
  });

  const [hideSystemUserActions, setHideAutomaticActions] = useHideAutomaticActions({
    scope: projectId,
  });

  const projectIds = React.useMemo(() => [projectId], [projectId]);
  const activityEvents = useActivityEvents(project.id);
  React.useEffect(() => {
    if (eventId === null) return;
    setFilteredValues(current =>
      current.eventIds.length === 1 && current.eventIds[0] === eventId
        ? current
        : { ...current, eventIds: [eventId] },
    );
  }, [eventId, setFilteredValues]);
  const eventIds = React.useMemo(
    () =>
      mapActivityEventFilterValues(
        eventId === null ? filteredValues.eventIds : [eventId],
        activityEvents,
      ),
    [activityEvents, eventId, filteredValues.eventIds],
  );

  const { activities, showUnread, fetchMore, unreadCount, loading, hasMore } = useActivities(
    projectIds,
    filteredValues.types,
    filteredValues.authors,
    eventIds,
    hideSystemUserActions,
  );
  const projectsIndex = useIndexedProjects();

  const activityRef = React.useRef<HTMLDivElement>(null);
  useHeaderHeight(activityRef, FILTER_ACTIVITY_HEIGHT_CSS_VARIABLE);

  return (
    <>
      <FilterWrapper ref={activityRef}>
        <Filters
          eventId={eventId}
          filteredValues={filteredValues}
          setFilteredValues={setFilteredValues}
          projectId={projectId}
          hideSystemUserActions={hideSystemUserActions}
          setHideAutomaticActions={setHideAutomaticActions}
        />
        {unreadCount ? <RefreshButton showUnread={showUnread} unreadCount={unreadCount} /> : null}
      </FilterWrapper>
      <ActivityWrapper>
        {activities.length === 0 ? (
          <BlankStateV2
            className={ActivityBlankCss}
            picto={<Circles />}
            title={t("activity.blank_state.title")}
            subtitle={t("activity.blank_state.project_description")}
          />
        ) : (
          <>
            <ActivityComponent
              projectsIndex={projectsIndex}
              activities={activities}
              timeZone={project.timezone}
              embeded
            />
            <More
              disabled={!hasMore || loading}
              onClick={hasMore && !loading ? fetchMore : undefined}
            >
              {t("activity.body.see_more")}
              {loading && <Spinner />}
            </More>
          </>
        )}
      </ActivityWrapper>
    </>
  );
}

export default React.memo(ProjectActivity);
